import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MagnifyingGlass, Crosshair, List } from "@phosphor-icons/react";
import { api, CATEGORIES } from "@/lib/api";
import { PersonCard } from "@/components/PersonCard";
import { EditorialMarquee } from "@/components/Marquee";
import { useLang } from "@/i18n/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.2 + i * 0.12, ease: [0.76, 0, 0.24, 1] },
  }),
};

export default function Catalog() {
  const { t, tc } = useLang();
  const [people, setPeople] = useState([]);
  const [stats, setStats] = useState(null);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 160]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== "All") params.category = category;
      if (search) params.search = search;
      const [pRes, sRes] = await Promise.all([
        api.get("/people", { params }),
        api.get("/stats"),
      ]);
      setPeople(pRes.data);
      setStats(sRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [category, search]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const titleLines = t("hero.titleLines");
  const manifesto = t("manifesto.chapters");

  return (
    <div data-testid="catalog-page">
      {/* HERO */}
      <section className="relative flex min-h-screen flex-col justify-end overflow-hidden border-b border-border px-6 md:px-12 pb-10 pt-28">
        <motion.div
          style={{ y: heroY }}
          className="pointer-events-none absolute right-6 top-28 md:right-12 md:top-32 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground text-right leading-loose"
        >
          <div className="flex items-center justify-end gap-2">
            <Crosshair size={14} className="text-primary" /> {t("hero.metaArchive")}
          </div>
          <div>{t("hero.metaRecord")}</div>
        </motion.div>

        <motion.div
          style={{ opacity: heroOpacity }}
          className="flex flex-col gap-10 md:flex-row-reverse md:items-center md:justify-between md:gap-12"
        >
          <img
            src="/hero-figure.png"
            alt="ПОЗОР"
            data-testid="hero-image"
            className="w-[17.5rem] sm:w-[22.5rem] lg:w-[37.5rem] h-auto shrink-0 object-contain self-center"
          />
          <div className="md:flex-1">
          <div className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-primary">
            <span className="reveal-mask inline-block">
              <motion.span
                variants={line}
                custom={0}
                initial="hidden"
                animate="show"
                className="inline-block"
              >
                {t("hero.subtitle")}
              </motion.span>
            </span>
          </div>

          <h1 className="font-display font-black uppercase leading-[0.82] tracking-tighter text-foreground">
            {titleLines.map((tokens, i) => (
              <span key={i} className="reveal-mask block">
                <motion.span
                  variants={line}
                  custom={i + 1}
                  initial="hidden"
                  animate="show"
                  className="block text-6xl sm:text-8xl lg:text-[10vw]"
                >
                  {tokens.map((tok, j) => (
                    <span key={j} className={tok.hl ? "text-primary" : undefined}>
                      {tok.text}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mt-8 max-w-xl font-mono text-sm leading-relaxed text-muted-foreground reveal-mask">
            <motion.p variants={line} custom={3} initial="hidden" animate="show">
              {t("hero.description")}
            </motion.p>
          </div>
          </div>
        </motion.div>
      </section>

      {/* STATS BAR */}
      <section
        className="grid grid-cols-2 border-b border-border md:grid-cols-4"
        data-testid="stats-bar"
      >
        {[
          { label: t("stats.total"), value: stats?.total ?? "—" },
          { label: t("stats.categories"), value: stats?.categories ?? "—" },
          {
            label: t("stats.updated"),
            value: stats?.last_updated
              ? new Date(stats.last_updated).toISOString().slice(0, 10)
              : "—",
          },
          { label: t("stats.status"), value: t("stats.statusValue") },
        ].map((s, i) => (
          <div
            key={i}
            className={`p-6 md:p-8 ${i < 3 ? "border-r border-border" : ""} ${i < 2 ? "border-b md:border-b-0 border-border" : ""}`}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              {s.label}
            </div>
            <div className="mt-3 font-display font-black text-3xl md:text-5xl tracking-tighter text-foreground">
              {s.value}
            </div>
          </div>
        ))}
      </section>

      <EditorialMarquee />

      {/* MANIFESTO */}
      <section className="border-b border-border px-6 md:px-12 py-20 md:py-28" data-testid="manifesto">
        <div className="mb-14 font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
          {t("manifesto.label")}
        </div>
        <div className="space-y-16">
          {manifesto.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
              className="grid grid-cols-1 gap-6 md:grid-cols-12 md:items-start border-t border-border pt-8"
            >
              <div className="md:col-span-3 font-display font-black text-6xl md:text-8xl leading-none text-accent">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h2 className="md:col-span-3 font-display font-extrabold uppercase tracking-tight text-3xl md:text-4xl text-foreground">
                {m.title}
              </h2>
              <p className="md:col-span-6 font-mono text-sm md:text-base leading-relaxed text-muted-foreground max-w-2xl">
                {m.body}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CATALOG CONTROLS */}
      <section className="border-b border-border px-6 md:px-12 py-10" data-testid="catalog-controls">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">
              {t("catalog.label")}
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl text-foreground">
              {t("catalog.title")}
            </h2>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3 border-b border-foreground pb-2 min-w-[240px]">
              <MagnifyingGlass size={18} weight="bold" className="text-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("catalog.search")}
                data-testid="search-input"
                className="w-full bg-transparent font-mono text-sm uppercase tracking-widest outline-none placeholder:text-muted-foreground"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                className="w-[200px] rounded-none border-foreground font-mono text-xs uppercase tracking-[0.2em]"
                data-testid="category-select"
              >
                <SelectValue placeholder={t("catalog.categoryPlaceholder")} />
              </SelectTrigger>
              <SelectContent className="rounded-none font-mono">
                <SelectItem value="All" data-testid="cat-option-all">
                  {t("catalog.allCategories")}
                </SelectItem>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`cat-option-${c}`}>
                    {tc(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section data-testid="catalog-grid">
        {loading ? (
          <div className="py-32 text-center font-mono text-sm uppercase tracking-[0.3em] text-muted-foreground">
            {t("catalog.loading")}
          </div>
        ) : people.length === 0 ? (
          <div className="py-32 text-center font-mono text-sm uppercase tracking-[0.3em] text-muted-foreground">
            {t("catalog.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 border-l-0 sm:grid-cols-2 lg:grid-cols-4 border-t border-border">
            {people.map((p, i) => (
              <PersonCard key={p.id} person={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border px-6 md:px-12 py-16" data-testid="footer">
        <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <Link
            to="/"
            className="font-display font-black uppercase tracking-tighter text-3xl"
            data-testid="logo-link"
          >
            Shame<span className="text-primary">.</span>today
          </Link>
          <nav className="flex items-center gap-6 font-mono text-xs uppercase tracking-[0.2em] text-foreground">
            <Link
              to="/admin"
              className="transition-opacity hover:opacity-60 flex items-center gap-2"
              data-testid="nav-admin"
            >
              <List size={14} weight="bold" /> {t("nav.admin")}
            </Link>
          </nav>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            {t("footer.tagline").replace("{year}", new Date().getFullYear())}
          </div>
        </div>
      </footer>
    </div>
  );
}
